/**
 * whatsappScope.js — Scope and cache helpers for WhatsApp multi-account / multi-number (Task 10b).
 *
 * Rules & invariants:
 * - scopeRev is an OPAQUE string (a hash of the visible key set), not a counter:
 *   never compare scopeRev values for order.
 * - Decide "older" by a client request sequence number (a late response from an
 *   earlier request is dropped).
 * - Decide "replace" by full:true or scopeRev !== the current one.
 * - Chat caches are keyed by (companyId, scopeKey).
 * - A response with a changed scopeRev or full:true REPLACES the list (does not merge).
 * - The selected chat is cleared when it is no longer in the list.
 * - The messages, activity and analysis caches of chats that left are cleared.
 * - A response to an earlier request, or to a scope the user has since left, is dropped.
 * - Feature off: responses carry no scopeRev and behaviour is exactly today's merge.
 */

let clientSeqCounter = 0;

export function nextSeq() {
  clientSeqCounter += 1;
  return clientSeqCounter;
}

export function resetSeqForTest(val = 0) {
  clientSeqCounter = val;
}

export function makeScopeKey(companyId, scopeKey) {
  const c = companyId ?? 'default';
  const s = scopeKey ?? 'all';
  return `${c}:${s}`;
}

export function isResponseStale({
  seq,
  scopeKey,
  companyId,
  scopeRev,
  currentScopeKey,
  currentCompanyId,
  currentScopeRev,
  lastProcessedSeq,
  since,
}) {
  // If user has left this scope, drop it
  if (
    currentScopeKey !== undefined &&
    currentScopeKey !== null &&
    scopeKey !== undefined &&
    scopeKey !== null &&
    scopeKey !== currentScopeKey
  ) {
    return true;
  }
  if (
    currentCompanyId !== undefined &&
    currentCompanyId !== null &&
    companyId !== undefined &&
    companyId !== null &&
    companyId !== currentCompanyId
  ) {
    return true;
  }
  if (
    seq !== undefined &&
    seq !== null &&
    lastProcessedSeq !== undefined &&
    lastProcessedSeq !== null &&
    seq < lastProcessedSeq
  ) {
    // Delta polls (with since) always drop when older than lastProcessedSeq
    if (since) return true;
    // Older revision responses drop when older than lastProcessedSeq
    if (
      currentScopeRev !== undefined &&
      currentScopeRev !== null &&
      scopeRev !== undefined &&
      scopeRev !== null &&
      scopeRev !== currentScopeRev
    ) {
      return true;
    }
  }
  return false;
}

export function switchScope(state, companyId, scopeKey) {
  if (!state.scopeCache) state.scopeCache = {};
  if (!state.lastProcessedSeqByScope) state.lastProcessedSeqByScope = {};

  const currentKey = makeScopeKey(state.currentCompanyId, state.currentScopeKey);

  // Cache current scope's chats and revision
  if (state.currentScopeKey !== null || state.currentCompanyId !== null) {
    state.scopeCache[currentKey] = {
      chats: state.chats || [],
      scopeRev: state.scopeRev || null,
      lastSeq: state.lastProcessedSeqByScope[currentKey] || 0,
    };
  }

  const nextKey = makeScopeKey(companyId, scopeKey);
  state.currentCompanyId = companyId ?? null;
  state.currentScopeKey = scopeKey ?? null;

  // Restore from cache if previously visited, otherwise reset for new scope
  if (state.scopeCache[nextKey]) {
    state.chats = state.scopeCache[nextKey].chats || [];
    state.scopeRev = state.scopeCache[nextKey].scopeRev || null;
  } else {
    state.chats = [];
    state.scopeRev = null;
  }
}

export function applyChatsFulfilled(state, action) {
  const payload = action.payload;
  const arg = action.meta?.arg || {};

  // Extract raw chat list and multi-number metadata
  const rawChats = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.chats)
      ? payload.chats
      : Array.isArray(payload?.data)
        ? payload.data
        : [];

  const responseScopeRev = payload?.scopeRev ?? null;
  const responseFull = Boolean(payload?.full);
  const responseSeq = payload?.seq ?? arg.seq ?? null;
  const responseScopeKey = payload?.scopeKey ?? arg.scopeKey ?? null;
  const responseCompanyId = payload?.companyId ?? arg.companyId ?? null;

  const currentScopeKey = state.currentScopeKey;
  const currentCompanyId = state.currentCompanyId;
  const cacheKey = makeScopeKey(
    responseCompanyId ?? currentCompanyId,
    responseScopeKey ?? currentScopeKey
  );

  if (!state.scopeCache) state.scopeCache = {};
  if (!state.lastProcessedSeqByScope) state.lastProcessedSeqByScope = {};

  const lastSeq = state.lastProcessedSeqByScope[cacheKey] || state.lastProcessedSeq || 0;

  // Check stale response conditions:
  // 1. Response for a scope the user has since left
  if (
    currentScopeKey !== null &&
    currentScopeKey !== undefined &&
    responseScopeKey !== null &&
    responseScopeKey !== undefined &&
    responseScopeKey !== currentScopeKey
  ) {
    return;
  }
  if (
    currentCompanyId !== null &&
    currentCompanyId !== undefined &&
    responseCompanyId !== null &&
    responseCompanyId !== undefined &&
    responseCompanyId !== currentCompanyId
  ) {
    return;
  }

  const hasSince = Boolean(payload?.since || arg.since);

  // 2. Delayed response from an earlier sequence:
  // - Delta polls (with since) keep the drop-older rule.
  // - An older response from a superseded scopeRev is dropped.
  // - A read WITHOUT since (search / full load) in the current scope always applies.
  const isStaleRevision =
    responseScopeRev !== null && state.scopeRev !== null && responseScopeRev !== state.scopeRev;

  if (responseSeq !== null && responseSeq !== undefined && responseSeq < lastSeq) {
    if (hasSince || isStaleRevision) {
      return;
    }
  }

  // Advance sequence bookmark (raise the applied mark to its own seq so an older poll still in flight is dropped)
  if (responseSeq !== null && responseSeq !== undefined) {
    state.lastProcessedSeqByScope[cacheKey] = Math.max(lastSeq, responseSeq);
    state.lastProcessedSeq = Math.max(state.lastProcessedSeq || 0, responseSeq);
  }

  // Feature off check: responses carry no scopeRev and current state has no scopeRev
  const isFeatureOff =
    responseScopeRev === null && state.scopeRev === null && !responseFull && !arg.scopeRev;

  if (isFeatureOff) {
    // Normal legacy merge
    const chatMap = new Map();
    (state.chats || []).forEach((c) => chatMap.set(c.id, c));
    rawChats.forEach((c) => {
      if (chatMap.has(c.id)) {
        chatMap.set(c.id, { ...chatMap.get(c.id), ...c });
      } else {
        chatMap.set(c.id, c);
      }
    });
    state.chats = Array.from(chatMap.values()).sort(
      (a, b) => new Date(b.last_message_at) - new Date(a.last_message_at)
    );
    return;
  }

  // Feature is ON:
  // Decide "replace" by full:true or scopeRev !== the current one
  const shouldReplace =
    responseFull ||
    (responseScopeRev !== null && state.scopeRev !== null && responseScopeRev !== state.scopeRev);

  if (shouldReplace) {
    const newChats = [...rawChats].sort(
      (a, b) => new Date(b.last_message_at) - new Date(a.last_message_at)
    );
    const newIds = new Set(newChats.map((c) => c.id));
    const previousChats = state.chats || [];

    // Identify chats that left the authorized scope
    const revokedChats = previousChats.filter((c) => !newIds.has(c.id));

    // Clear caches for chats that left:
    // 1. messagesByChat
    if (state.messagesByChat) {
      revokedChats.forEach((c) => {
        delete state.messagesByChat[c.id];
      });
    }

    // 2. withinWindowByChat
    if (state.withinWindowByChat) {
      revokedChats.forEach((c) => {
        delete state.withinWindowByChat[c.id];
      });
    }

    // 3. activeChatId & activity if the active chat was revoked
    if (state.activeChatId && !newIds.has(state.activeChatId)) {
      state.activeChatId = null;
      state.activity = null;
    }

    state.chats = newChats;
  } else {
    // Delta poll within same revision: merge updates/new chats
    const chatMap = new Map();
    (state.chats || []).forEach((c) => chatMap.set(c.id, c));
    rawChats.forEach((c) => {
      if (chatMap.has(c.id)) {
        chatMap.set(c.id, { ...chatMap.get(c.id), ...c });
      } else {
        chatMap.set(c.id, c);
      }
    });
    state.chats = Array.from(chatMap.values()).sort(
      (a, b) => new Date(b.last_message_at) - new Date(a.last_message_at)
    );
  }

  // Counted here, not in a component: the store applies every answer even when
  // React batches several into one render (A→B→A must count as two changes).
  if (responseScopeRev !== null) {
    if (responseScopeRev !== state.scopeRev) {
      state.scopeRevChanges = (state.scopeRevChanges || 0) + 1;
    }
    state.scopeRev = responseScopeRev;
  }
  state.scopeChatAnswers = (state.scopeChatAnswers || 0) + 1;

  // Cache by (companyId, scopeKey)
  state.scopeCache[cacheKey] = {
    chats: state.chats,
    scopeRev: state.scopeRev,
    lastSeq: state.lastProcessedSeqByScope[cacheKey] || 0,
  };
}
